import React, { useState, useEffect } from 'react';
import { Theme, } from '../types/theme';
import { Sparkles } from "lucide-react";
import chatbotImg from "../assets/Priya-80x80.png";


interface FloatingChatButtonProps {
  onClick: () => void;
  theme: Theme;
  agent_name: string;
}

const FloatingChatButton: React.FC<FloatingChatButtonProps> = ({
  onClick,
  theme,
  agent_name
}) => {
  const [showMessage, setShowMessage] = useState(false);

  useEffect(() => {
    const showTimer = setTimeout(() => setShowMessage(true), 3000);
    return () => {
      clearTimeout(showTimer);
    };
  }, []);

  const primaryColor = theme?.primaryColor || '#2563eb';

  // helpers to convert hex -> rgba
  const hexToRgb = (hex: string) => {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    const int = parseInt(h, 16);
    return {
      r: (int >> 16) & 255,
      g: (int >> 8) & 255,
      b: int & 255
    };
  };

  const hexToRgbaString = (hex: string, alpha = 1) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  // const glowColor = hexToRgbaString(primaryColor, 0.32);

  return (
    <div className="fixed bottom-6 right-6 z-50 pointer-events-auto">
      {/* Speech bubble message */}
      {showMessage && (
        <div
          className="absolute bottom-20 right-0 mb-2 animate-fadeIn"
          style={{
            // responsive width with sensible min/max so text doesn't overflow
            width: 'max-content',
            zIndex: 60
          }}
        >
          <div
            className="relative bg-white rounded-2xl shadow-lg px-4 py-3"
            style={{
              boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
              // prevent text selection causing weird width shifting
              wordWrap: 'break-word'
            }}
            role="dialog"
            aria-live="polite"
          >
            {/* Close button */}
            <button
              onClick={() => setShowMessage(false)}
              aria-label="Close chat message"
              className="absolute -top-2 -right-2 w-4 h-4 bg-gray-400 hover:bg-gray-500 rounded-full flex items-center justify-center text-white text-sm transition-colors duration-200"
              style={{
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)'
              }}
            >
              x
            </button>

            {/* Message content */}
            <div className="flex items-center space-x-3">
              <div
                className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-white"
                style={{ backgroundColor: primaryColor }}
                aria-hidden
              >
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-gray-800 text-sm font-medium leading-tight">
                  Hey! Drop a word, let's talk.
                </p>
              </div>
            </div>

            {/* Speech bubble tail */}
            <div
              className="absolute"
              style={{
                top: '100%',
                right: '56px', // tuned to sit above the avatar nicely
                width: 0,
                height: 0,
                borderLeft: '8px solid transparent',
                borderRight: '8px solid transparent',
                borderTop: '8px solid white',
                filter: 'drop-shadow(0 -1px 0 rgba(0,0,0,0.03))'
              }}
            />
          </div>
        </div>
      )}

      {/* Circular avatar button with glowing border */}
      <div
        className="avatar-glow-wrapper"
        style={{
          display: 'inline-block',
          borderRadius: '9999px',
          background: 'transparent',
          boxShadow: `0 6px 20px ${hexToRgbaString(primaryColor, 0.12)}, 0 4px 18px rgba(0,0,0,0.15)`,
          animation: 'pulseGlow 3s ease-in-out infinite'
        }}
      >
        <button
          onClick={onClick}
          className="w-16 h-16 rounded-full border-4 border-white shadow-lg hover:scale-105 transition-all duration-300 overflow-hidden focus:outline-none"
          style={{
            backgroundColor: primaryColor,
            boxShadow: `0 6px 20px ${hexToRgbaString(primaryColor, 0.12)}, 0 4px 14px rgba(0,0,0,0.18)`
          }}
          aria-label={`Open chat with ${agent_name}`}
        >
          <img
            src={chatbotImg}
            alt={`${agent_name} avatar`}
            className="w-full h-full object-cover"
            style={{ display: 'block' }}
          />
        </button>
      </div>

      {/* Inline styles for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.28s ease-out; }

        @keyframes pulseGlow {
          0% {
            box-shadow: 0 6px 18px ${hexToRgbaString(primaryColor, 0.12)}, 0 4px 14px rgba(0,0,0,0.15);
            transform: translateY(0);
          }
          50% {
            box-shadow: 0 10px 30px ${hexToRgbaString(primaryColor, 0.22)}, 0 6px 22px rgba(0,0,0,0.12);
            transform: translateY(-1px);
          }
          100% {
            box-shadow: 0 6px 18px ${hexToRgbaString(primaryColor, 0.12)}, 0 4px 14px rgba(0,0,0,0.15);
            transform: translateY(0);
          }
        }
      `}</style>
    </div>

  );
};

export default FloatingChatButton;
